import {
  AfterViewInit,
  Component,
  EventEmitter, HostListener,
  Injector,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges, Type,
  ViewChild
} from "@angular/core";
import {DxGridOptions} from "../../domain/ui-options/dx-grid-options";
import {DxGridRow} from "../../domain/dx-grid-row";
import {forkJoin, Observable, Subject, Subscription} from "rxjs";
import {DxGridState} from "../../domain/dx-grid-state";
import {DxGridToolbarActionResolver} from "../../domain/providers/dx-grid-toolbar-action-resolver";
import {DxGridDatasource} from "../../domain/providers/dx-grid-datasource";
import {DxGridToolbarAction} from "../../domain/ui-options/toolbar/dx-grid-toolbar-action";
import {DxAppError} from "../../../dx-core/domain/error/dx-app-error";
import {DxListToolbarAction} from "../../../dx-lists/domain/ui-options/list/toolbar/dx-list-toolbar-action";
import {DxGridToolbarActionType} from "../../domain/ui-options/toolbar/dx-grid-toolbar-action-type";
import {DxAppEvent} from "../../../dx-core/domain/events/dx-app-event";
import {GridOptions, GridApi, ColumnApi, RowNode} from "ag-grid-community";
import {AgGridAngular} from "ag-grid-angular";
import {AgGridDatasource} from "../../services/ag-grid-datasource";
import {DxGridUtils} from "../../domain/dx-grid-utils";
import {AgGridUtils} from "../../domain/ag-grid/ag-grid-utils";
import {DxGridModalsProvider} from "../../domain/providers/dx-grid-modals-provider";
import {DxGridRowFactory} from "../../domain/providers/dx-grid-row-factory";
import {DxGridDataType} from "../../domain/dx-grid-data-type";
import {DxNumericCellEditorComponent} from "./editors/nuneric/dx-numeric-cell-editor.component";
import {DxLinkCellRenderComponent} from "./renders/link/dx-link-cell-render.component";
import {DxCurrencyCellRenderComponent} from "./renders/currency/dx-currency-cell-render.component";
import {DxNciService} from "../../../dx-nci-core/services/dx-nci.service";
import {DxNciDictionary} from "../../../dx-nci-core/domain/dx-nci-dictionary";
import {DxDictionaryCellRenderComponent} from "./renders/dictionary/dx-dictionary-cell-render.component";
import {DxGridFilter} from "../../domain/filter/dx-grid-filter";
import {DxCheckboxCellRenderComponent} from "./renders/checkbox/dx-checkbox-cell-render.component";
import {DxCheckboxCellEditorComponent} from "./editors/checkbox/dx-checkbox-cell-editor.component";
import {DxDatetimeCellRenderComponent} from "./renders/datetime/dx-datetime-cell-render.component";
import {map} from "rxjs/operators";
import {DxDictionaryFloatingFilterComponent} from "./renders/dictionary-filter/dx-dictionary-floating-filter.component";
import {debounce} from "../../../dx-core/utiles/debounce";



@Component({
  selector: 'dx-grid',
  templateUrl: './dx-grid-component.html',
  styleUrls: ['./dx-grid-component.css'],
  providers: [AgGridDatasource]
})
export class DxGridComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  /** Источник данных списка. */
  protected _dataSource: DxGridDatasource;
  /** Источник данных для справочников НСИ (пока завязка на конкретный сервис). */
  protected _dictDataSource: DxNciService;
  /** Резолвер действий на панели инструментов списка. */
  protected _actionResolver: DxGridToolbarActionResolver;
  /** Провайдер модальных окон для списка. */
  protected _modalsProvider: DxGridModalsProvider;
  /** Фабрика по созданию \ копированию строк. */
  protected _rowFactory: DxGridRowFactory;

  /**
   * Текущее состояние гридп: количество записей, выделенные\удаленные\обновленные строки и тд.
   * @name _state
   * @type {DxGridState}
   */
  private readonly _state: DxGridState;

  /**
   * Обсервэбл-объект на состояние грида. Позволяет организовать отсылку обновленного состояния по подписке.
   * @name stateObservable
   * @type {Subject<DxGridState>}
   */
  public stateObservable: Subject<DxGridState>;

  /** Флаг индикации загрузки списка */
  private _isLoading: boolean = true;

  /**
   * Мапа обработчиков действий на тулбаре
   * @name _toolbarActionHandlers
   * @type {Map<string, (action: DxGridToolbarAction) => void>}
   */
  protected _toolbarActionHandlers: Map<string, (action: DxGridToolbarAction) => void>;

  /**
   * Мапа обработчиков действий на тулбаре.
   * @name _cellEditors
   * @type {Map<string, Type<any>>}
   */
  private readonly _cellEditors: Map<string, Type<any>>;

  /**
   * Мапа копонентов-рендеров для ячеек грида.
   * Согласно документации на AgGrid, в случае с Angular, React для рендеринга ячеек могут использваться
   * только комопненты.
   *
   * @name _cellEditors
   * @type {Map<string, Type<any>>}
   */
  private readonly _cellRenders: Map<string, Type<any>>;

  /**
   * Мапа копонентов-рендеров для ячеек с фильтрами в шапке
   * Согласно документации на AgGrid, в случае с Angular, React для рендеринга ячеек могут использваться
   * только комопненты.
   *
   * @name _floatingFilters
   * @type {Map<string, Type<any>>}
   */

  private readonly _floatingFilters: Map<string, Type<any>>;

  @ViewChild(AgGridAngular, {static: false})
  public agGrid: AgGridAngular;

  /** Опции копонента agGrid. */
  public agGridOptions: GridOptions;

  /**
   * Ссылка на API AgGrid.
   * @name _agGridApi
   * @type {GridApi}
   */
  private _agGridApi: GridApi;

  /**
   * Ссылка на API колонок AgGrid.
   * @name _agGridColumnApi
   * @type {ColumnApi}
   */
  private _agGridColumnApi: ColumnApi;

  /**
   * Датасорс для реализации серверной модели данных компонента AgGrid.
   * @name _agGridDataSource
   * @type {AgGridDatasource}
   */
  private _agGridDataSource: AgGridDatasource;

  /**
   * Массив данных, передаваемых гриду в режиме клиентской модели данных.
   * Добавлен чтобы вынести преобразование данных в формат AgGrid на этап обработки события `gridReady`.
   * @name _data
   * @type {DxGridRow[]}
   */
  private _data: DxGridRow[];

  /**
   * Мапа справочников для грида.
   * 1. Должна быть передана из вне при работе в режиме клиентской модели данных
   * 2. Заполняется через фетч справочников при помощи модуля НСИ. Иденты справочников берутся из метаданных колонок.
   * @name _gridDicts
   * @type {Map<string, DxNciDictionary>}
   */
  private _gridDicts: Map<string, DxNciDictionary>;

  /**
   * Массив строк в формате копонента AgGrid.
   * @name _rows
   * @type {any[]}
   */
  private _rows: any[];

  /** Массив подписок для корректной отписки */
  private _subscriptions: Subscription[];

  /**
   * Логика управления доступностью можеть быть сложной, поэтому доступ
   * к этому полю через геттер.
   */
  protected _isDisabled: boolean = false;

  get isDisabled(): boolean {
    return this._isDisabled;
  }

  constructor(protected _injector: Injector,
              private _nciService: DxNciService) {
    this._state = new DxGridState();
    this.stateObservable = new Subject();
    this._toolbarActionHandlers = new Map();
    this._rows = [];
    this._subscriptions = [];
    this._cellEditors = new Map();
    this._cellRenders = new Map();
    this._floatingFilters = new Map();
    this._initCellEditors();
    this._initCellRenders();
    //this._initFloatingFilters();
  }

  /* ============================================================================================================= */
  /*                                          Инпуты комопонента                                                   */
  /* ============================================================================================================= */

  /** Опции грида. */
  @Input()
  public options: DxGridOptions;

  @Input()
  public rowClassRules: Object = {};

  /** Свойство доступности */
  @Input()
  set isDisabled(value: boolean) {
    this._isDisabled = value;
  }

  /** Данные грида (для работы в режиме клиенской модели данных) */
  @Input()
  set data(value: DxGridRow[]) {
    this._data = value;
  }

  /** Справочники для грида (при работе в режиме клиентской модели данных). */
  @Input()
  set gridDicts(value: Map<string, DxNciDictionary>) {
    this._gridDicts = value;
  }

  /**
   * node: The rowNode in question.
   * data: The data for the row.
   * api: The grid api.
   * context: The grid context.
   */
  @Input()
  getRowHeight: (params: {node, data, api, context}) => number;

  @Input()
  rowHeight: number;

  /* ============================================================================================================= */
  /*                                          События комопонента                                                  */
  /* ============================================================================================================= */

  /**
   * У панели инструментов грида есть два режима работы:
   *
   * 1. Внутренняя обработка действий с панели инструментов через втроенные методы.
   *
   * 2. Режим внешнего обработчика действий панели инструментов. Это добавляет гибкости при встраивании в дизайн
   *    + не надо тащить всякое говно и костыли в код компонента, просто реализовав обработку
   *    снаружи списка в прикланом компоненте.
   *
   * Короче можно использовать механизмы создания и управления тулбаром, предоставляемые гридом,
   * но обрабатывать действия с панели инструментов вне грида.
   */
  @Output()
  public toolbarAction: EventEmitter<DxAppEvent> = new EventEmitter();

  /** Событие выбрасывается при сбросе фильтров грида по кнопке "Сбросить фильтры" на панели инструментов. */
  @Output()
  public filtersFlush: EventEmitter<DxAppEvent> = new EventEmitter();

  /** Событие выбрасывается при выделении строки в гриде. */
  @Output()
  public rowsSelect: EventEmitter<DxAppEvent> = new EventEmitter();

  /** Событие выбрасывается при снятии выделения со строки в гриде. */
  @Output()
  public rowsDeselect: EventEmitter<DxAppEvent> = new EventEmitter();

  /** Событие выбрасывается при удалении строки в гриде. */
  @Output()
  public rowsRemove: EventEmitter<DxAppEvent> = new EventEmitter();

  @Output()
  public rowDataUpdated: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Событие выбрасывается по завершению инициализации грида.
   * Но данные еще могут быть не подтянуты при серверной модели данных.
   */
  @Output()
  public gridReady: EventEmitter<DxAppEvent> = new EventEmitter();

  /** Событие выбрасывается при обновлении грида через тулбар. */
  @Output()
  public gridRefresh: EventEmitter<DxAppEvent> = new EventEmitter();

  /**
   * Событие выбрасывается при добавлении, копировании, удалении, редактировании и тд. строк.
   * Это понадобится при использовании грида на формах, чтобы сигнализировать форме о наличии измений.
   */
  @Output()
  public gridDirty: EventEmitter<DxAppEvent> = new EventEmitter();

  /** Событие выбрасывается года грид получил данные. */
  @Output()
  public dataArrived: EventEmitter<DxAppEvent> = new EventEmitter();

  /** событие выбрасывается в результате редактирования значения в ячейке */
  @Output()
  public cellValueChanged: EventEmitter<DxAppEvent> = new EventEmitter();

  @debounce()
  @HostListener('window:resize', ['$event'])
  onWindowResize(event) {
    this.resize();
  }

  /* ============================================================================================================= */
  /*                                          Хуки жизнненого цилка                                                */
  /* ============================================================================================================= */

  ngOnInit(): void {
    this._init();
  }

  ngAfterViewInit(): void { }

  ngOnDestroy(): void {
    this._destroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Для клиентского режима работы грида организуем возможность обновления данных
    // через инпут.
    if(!this.options.useServerSideDataModel && changes.data) {
      const dataChange = changes.data;
      if(dataChange && dataChange.currentValue) {
        this.fill(dataChange.currentValue);
      }
    }
  }


  /* ============================================================================================================= */
  /*                                          Обработчики событий грида                                            */
  /* ============================================================================================================= */

  /** Обработчик дейсвия на панели нструментов. */
  public onToolbarAction(action: DxGridToolbarAction): void {
    // Если для грида определен внешний хэдлер для обработки действий на панели
    // инструментов, то просто транслируем акшен наружу.
    if(this.options.externalToolbarHandler) {
      this.toolbarAction.emit(new DxAppEvent('dx-grid', action));
    } else {
      this.handleToolbarAction(action);
    }
  }


  /** Обработчик события `gridReady` комопнента AgGrid. */
  public onAgGridReady(params: any): void {
    this._agGridApi = params.api;
    this._agGridColumnApi = params.columnApi;
    if(this.options.useServerSideDataModel) {
      let subscriptions = this._agGridDataSource.loadObservable.subscribe(isLoading => {
        this._handleRowDataLoad(isLoading);
      });
      this._subscriptions.push(subscriptions);

      subscriptions = this._agGridDataSource.loadErrorObservable.subscribe(error => {
        this._handleRowDataLoadError(error);
      });
      this._subscriptions.push(subscriptions);

      subscriptions = this._agGridDataSource.dictsDataObservable.subscribe(data => {
        this._gridDicts = data;
      });
      this._subscriptions.push(subscriptions);

      // Датасорс нужно передавать именно после события gridReady через api
      // Передача через инпут комопнента приводит к двойному запросу данных при инициализации.
      // Фетч в этом случае пойдет автоматически через внутренние механизмы AgGrid
      this._agGridApi.setDatasource(this._agGridDataSource);
    }
    // Если используется клиентская модель данных, то
    // просто заполняем данные для грида
    else {

      this.fill(this._data);
    }
    this.gridReady.emit(new DxAppEvent('dx-grid', this.options.ident));
  }


  public onAgGridModelUpdated(event): void {
    // при использовании клиент-сайд данных данное событие срабатывает раньше GridReady и _agGridApi может быть undefined
    if (!this._agGridApi) this._agGridApi = event.api;
    if(this.options.useServerSideDataModel) {
      if(this.options.allowPagination) {
        this._state.rowsCount = this._agGridApi.paginationGetRowCount();
      } else {
        this._state.rowsCount = this._agGridApi.getInfiniteRowCount();
      }
    } else {
      if(this.options.allowPagination) {
        this._state.rowsCount = this._agGridApi.paginationGetRowCount();
      } else {
        this._state.rowsCount = this._agGridApi.getDisplayedRowCount();
      }
    }
    // this.agGrid.
    // this._agGridDataSource
    // this.fill(this._agGridDataSource.rowCount);
    this.dataArrived.emit(new DxAppEvent('dx-grid'));
    this.notifyStateObservers();
  }


  public onRowSelection(event): void {
    const selectedRows = this._agGridApi.getSelectedRows();
    if(selectedRows.length > 0) {
      let row: DxGridRow;
      selectedRows.forEach(agRow => {
        row = <DxGridRow> agRow;
        row.isSelected = true;
        this._state.addSelection(row)
      });
      this.rowsSelect.emit(new DxAppEvent('dx-grid', selectedRows));
    } else if(this._state.hasSelection) {
      this.rowsDeselect.emit(new DxAppEvent('dx-grid'));
      this._state.clearSelected();
    }
    this.notifyStateObservers();
  }


  public onFilterChange(event): void {
    this._state.filterModel = this._agGridApi.getFilterModel();
    this.notifyStateObservers();
  }


  public onCellValueChanges(event): void {
    const row: DxGridRow = <DxGridRow> event.data;
    if(row.isNew) {
      this._state.updateAddedRow(row);
    } else {
      this._state.addUpdatedRow(row);
    }
    this.cellValueChanged.emit(new DxAppEvent('dx-grid', row));
  }

  public onColumnResized(event): void {
    if (event.column && event.column.colDef.autoHeight)
      this._agGridApi.resetRowHeights();
  }


  /* ============================================================================================================= */
  /*                                          Методы доступа                                                       */
  /* ============================================================================================================= */

  get state(): DxGridState {
    return this._state;
  }

  get rows(): any[] {
    return this._rows;
  }

  get cellEditors(): Map<string, Type<any>> {
    return this._cellEditors;
  }

  get cellRenders(): Map<string, Type<any>> {
    return this._cellRenders;
  }

  get floatingFilters(): Map<string, Type<any>> {
    return this._floatingFilters;
  }

  get gridDicts(): Map<string, DxNciDictionary> {
    return this._gridDicts;
  }

  /* ============================================================================================================= */
  /*                                          Публичное API грида                                                  */
  /* ============================================================================================================= */


  /** Регистрирует источник данных списка */
  public registerDS(dataSource: DxGridDatasource): void {
    this._dataSource = dataSource;
  }

  /** Затирает ссылку на инстанс датасорса */
  public unregisterDS(): void {
    this._dataSource = null;
  }


  /** Уведомляет всех наблюдателей за внутренним состоянием списка. */
  public notifyStateObservers(): void {
    this.stateObservable.next(this._state);
    if(this._state.isDirty) {
      this.gridDirty.emit(new DxAppEvent('dx-grid'));
    }
  }


  /**
   * Регистрирует обработчик действия на панели инструментов.
   * @param {string} type - тип действия на панели инструментов, которое обрабатывает обработчик.
   * @param {(action: DxListToolbarAction) => void} handler - функция-обработчик.
   */
  public addToolbarActionHandler(type: string, handler: (action: DxGridToolbarAction) => void): void {
    if(!this._toolbarActionHandlers) this._toolbarActionHandlers = new Map;
    this._toolbarActionHandlers.set(type, handler);
  }


  /**
   * Удаляет обработчик действия на панели инструментов.
   * @param {string} type - тип действия на панели инструментов, которое обрабатывает обработчик.
   */
  public removeToolbarActionHandler(type: string): boolean {
    if(this._toolbarActionHandlers.has(type)) {
      this._toolbarActionHandlers.delete(type);
      return true;
    }
    return false;
  }


  /**
   * Диспетчиризирует обработку действия на панели инструментов, вызывая
   * соотвествующий обработчик. Если он был задан.
   * @param {DxListToolbarAction} action - действие на панели инструментов.
   */
  public handleToolbarAction(action: DxGridToolbarAction): void {
    if(this.options.externalToolbarHandler) {
      this.toolbarAction.emit(new DxAppEvent('dx-grid', action));
    } else if(this._toolbarActionHandlers.has(action.type)) {
      const handler = this._toolbarActionHandlers.get(action.type);
      handler.apply(this, [action]);
    }
  }


  /**
   * Заполняет массив элементов списка (`DxListItem`) на основании массива провайдеров данных для элементов.
   * @param {DxListItemDataProvider[]} data - массив провайдеров данных для элементов.
   */
  public fill(data: DxGridRow[]): void {
    if (data) {
      this._rows = data;
      this._state.rowsCount = this._rows.length;
      this.notifyStateObservers();
      this.dataArrived.emit(new DxAppEvent('dx-grid'));
    }
  }


  /**
   * Фильтрует грид на основе переданной мапы фильтров.
   * @param {Map<string, DxGridFilter>} filters - мапа фильров.
   */
  public filter(filters: Map<string, DxGridFilter>): void {
    // 1. Флушим фильтр в сэйте грида
    this._state.flushFilter();
    this.notifyStateObservers();
    // 2. Формируем новую модель фильтрации
    const filterModel = AgGridUtils.prepareFilterModel(this.options.columns, filters, this._gridDicts);
    // 3. Сеттим новую модель фильтрации. Изменение модели фильтрации вызвает внутри AgGrid генерирование
    //    события на обновление данных. Поэтому отдельно дергать onFilterChanged() из апи грида не нужно.
    this._agGridApi.setFilterModel(filterModel);
  }


  /** Сбрасывает фильтры грида. */
  public flushFilters(): void {
    // 1. Скидываем собственную модель фитрации в AgGrid
    //    Внури AgGrid это приводит к генерировнию собтия на обновление данных,
    //    поэтому отдельно дергать onFilterChanged() из апи грида не нужно.
    this._agGridApi.setFilterModel(null);
    // 2. Скидываем филтры в стэйте грида.
    this._state.flushFilter();
    this.notifyStateObservers();
  }


  /**
   * Обновляет данные списка на основе асинхронного фетча, если для списка был опереден датасорс.
   * Либо сбрасывает состояние списка на исходное.
   */
  public refresh(): void {
    if(this.options.useServerSideDataModel) {
      // Вызывает сброс кэша и перезагрузку для infinite-модели данных
      this._agGridApi.refreshInfiniteCache();
    } else {
      // Вызывает рефреш клиент-сайд модели данных с перевычислением групп, фильтров, сортировок.
      this._agGridApi.refreshClientSideRowModel();
    }
    this._agGridApi.deselectAll();
    this.clear();
  }


  /** Вызывает перерисовку списка без рефетча данных. */
  public redraw(): void {
    throw new DxAppError('Метод DxBaseListComponent.redraw() не имеет реализации!');
  }

  /** Вызывает ресайз грида по горизонтали (подгонку ширины колонок). */
  public resize(): void {
    this._agGridApi.sizeColumnsToFit();
  }


  public autoSize(): void {
    this._agGridColumnApi.autoSizeAllColumns();
  }


  /** При использовании клиентской модели данных после изменения исходных данных данным методом можно подпнуть грид,
   * чтобы он обновился */
  public afterUpdateClientData(): void {
    if (this._agGridApi) this._agGridApi.setRowData(this._rows);
  }


  /**
   * Выделяет строку в гриде.
   * @param {DxGridRow} row - объект строки.
   */
  public selectRow(row: DxGridRow): void {
    const nodes: RowNode[] = this._agGridApi.getSelectedNodes();
    const node = nodes.find(node => {
      return (<DxGridRow> node.data).getRowId() === row.getRowId();
    });

    if(node) {
      this._state.addSelection(row);
      this.notifyStateObservers();
      this._agGridApi.selectNode(node);
    }
  }


  /**
   * Удаляет строку из гриде.
   * @param {DxGridRow} row - объект строки.
   * @param {number} addIndex - индекс позиции для новой строки в гриде (По-умолчанию добавляет как первую строку).
   */
  public addRow(row: DxGridRow, addIndex: number = 0): void {
    this._state.addedRows.push(row);
    this.notifyStateObservers();
    this._agGridApi.updateRowData({add: [row], addIndex: addIndex});
  }


  /**
   * Удаляет строку из гриде.
   * @param {DxGridRow} row - объект строки
   */
  public removeRow(row: DxGridRow): void {
    if(this.options.useServerSideDataModel) {
      let msg: string = 'Грид работает с серверной моделью данных. ';
      msg += 'В этом режиме прямое удаление данных не возможно. Необходимо удалить строку в БД, ';
      msg += 'а потом обновить данные грида!';
      throw new DxAppError(msg);
    } else {
      if(this._state.isSelected(row)) {
        this._state.removeSelection(row);
      }
      this._state.removedRows.push(row);
      //this._rows.splice(this._rows.indexOf(row), 1);
      this.notifyStateObservers();
      this._agGridApi.updateRowData({remove: [row]});
    }
  }


  /** Очищает состояние списка и структуру данных */
  public clear(): void {
    this._state.flush();
    this.notifyStateObservers();
    if(this._data) this._data.length = 0;
    if(this._rows) this._rows.length = 0;
  }


  /** Показывает ошибку, возникшую при выполнении операции списком. */
  public showError(message): void {
    const msg = 'При работе списка возникла ошибка: ';
    this.hideLoader();
    if(this._modalsProvider) {
      this._modalsProvider.showError(msg + message);
    } else {
      // TODO: Тут можно показать noDataOverlay в котором отобразить ошибку
      //this._errorMessage = new DxListMessage([msg, message]);
    }
  }


  /** Показывает индикацию загрузки. */
  public showLoader(): void {
    if(!this._isLoading) {
      this._isLoading = true;
      this._agGridApi.showLoadingOverlay();
    }
  }


  /** Скрывает индикацию загрузки. */
  public hideLoader(): void {
    this._isLoading = false;
    this._agGridApi.hideOverlay();
  }

  /** Вызывает обновление опций ag-грида в соответствии с изменениями в опциях грида */
  public refreshColumnDefs(): void {
    this.agGridOptions = AgGridUtils.buildAgGridOptions(this.options, this);
    if (this._agGridApi) {
      this._agGridApi.setColumnDefs(this.agGridOptions.columnDefs);
    }
  }


  /* ============================================================================================================= */
  /*                                          Приватная логика грида                                               */
  /* ============================================================================================================= */

  /** Инициализирует компонент. */
  protected _init(): void {
    // TODO: Пока так. Наверно, имее смысл дописать фвормирование дефолтных настроек.
    const error = DxGridUtils.verifyOptions(this.options);
    if(error) {
      let msg: string = 'Функционаирование грида не возможно! ';
      msg += 'Обнаружены критические ошибки конфигурации грида: ' + error.join(', ') + '.';
      throw new DxAppError(msg);
    } else {
      this._state.allowMultiSelect = this.options.allowMultiSelect;
      this.agGridOptions = AgGridUtils.buildAgGridOptions(this.options, this);
      this._bind();

      // Если грид работает в режиме серверной модели данных, то получаем инстансы датасорсов.
      if(this.options.useServerSideDataModel) {
        // Создаем внутррений датасорс комопнента AgGrid,
        // используемый для работы серверной модели данных.
        this._agGridDataSource = this._injector.get(AgGridDatasource);
        this._agGridDataSource.dxGridOptions = this.options;

        // Получаем инстанс прикладного датасорса
        const dataSources = this._injector.get<DxGridDatasource>(<any> this.options.dataSourceIdent);
        this._dataSource = dataSources[0];
        this._agGridDataSource.dxGridDS = this._dataSource;

        if(this.options.hasDictColumns) {
          this._dictDataSource = this._injector.get(DxNciService);
          this._agGridDataSource.dxGridDictsDS = this._dictDataSource;
          this._agGridDataSource.loadDicts = true;
        }
      } else { // загрузка справочников для клиентской модели грида
        if(this.options.hasDictColumns) {
          this._fetchDicts().subscribe(dictsMap => {
            this._gridDicts = dictsMap;
          });
        }
      }
    }

    // Фабрика экземляров строк необходима только если грид используется со своим тулбаром и
    // на нем опеределены операции добавления новой строки и копирования существующей.
    if(this.options.showToolbar) {
      const buttons = this.options.toolbar.buttons.filter(btn => {
        return btn.action.type === DxGridToolbarActionType.ADD
          || btn.action.type === DxGridToolbarActionType.COPY;
      });
      if(buttons && buttons.length > 0) {
        // Получаем инстанс фабрики для создания \ копирования строк
        const factories = this._injector.get<DxGridRowFactory>(<any> this.options.rowFactoryIdent);
        this._rowFactory = factories[0];
      }
    }

    if(this.options.modalsProviderIdent) {
      const modalProviders = this._injector.get<DxGridModalsProvider>(<any> this.options.modalsProviderIdent);
      this._modalsProvider = modalProviders[0];
    }
  }


  protected _initCellRenders(): void {
    this._cellRenders.set(DxGridDataType.LINK, DxLinkCellRenderComponent);
    this._cellRenders.set(DxGridDataType.CURRENCY, DxCurrencyCellRenderComponent);
    this._cellRenders.set(DxGridDataType.DICTIONARY, DxDictionaryCellRenderComponent);
    this._cellRenders.set(DxGridDataType.CHECKBOX, DxCheckboxCellRenderComponent);
    this._cellRenders.set(DxGridDataType.DATE, DxDatetimeCellRenderComponent);
    this._cellRenders.set(DxGridDataType.DATE_TIME, DxDatetimeCellRenderComponent);
    this._cellRenders.set(DxGridDataType.TIME, DxDatetimeCellRenderComponent);
  }

  protected _initCellEditors(): void {
    this._cellEditors.set(DxGridDataType.INT, DxNumericCellEditorComponent);
    this._cellEditors.set(DxGridDataType.DOUBLE, DxNumericCellEditorComponent);
    this._cellEditors.set(DxGridDataType.CURRENCY, DxNumericCellEditorComponent);
    this._cellEditors.set(DxGridDataType.CHECKBOX, DxCheckboxCellEditorComponent);
  }

  protected _initFloatingFilters(): void {
    this._floatingFilters.set(DxGridDataType.DICTIONARY, DxDictionaryFloatingFilterComponent);
  }


  /** Биндить различного рода обработчики. */
  protected _bind(): void {
    if(this.options.showToolbar || this.options.externalToolbarHandler) {
      this.options.toolbar.buttons.forEach(btn => {
        this._bindToolbarActionHandlers(btn.action);
      });
    }
  }


  /** Очищает ресурсы, задействованные компонентом. */
  protected _destroy(): void {
    // ОБЯЗАТЕЛЬНО!!!
    this.unregisterDS();
    this._toolbarActionHandlers.clear();
    this._subscriptions.forEach(item => item.unsubscribe());
    this._cellEditors.clear();
    this._cellRenders.clear();
    this._floatingFilters.clear();
  }


  /**
   * Регистрирует обработчики действий панели инструментов, поддерживаемые комопнентом `DxList`.
   * @param {DxListToolbarAction} action - действие, навешанное на кнопку панели инструментов.
   */
  protected _bindToolbarActionHandlers(action: DxGridToolbarAction): void {
    switch (action.type) {
      case DxGridToolbarActionType.REFRESH:
        this._toolbarActionHandlers.set(DxGridToolbarActionType.REFRESH, this._handleRefreshAction);
        break;
      case DxGridToolbarActionType.FLUSH_FILTERS:
        this._toolbarActionHandlers.set(DxGridToolbarActionType.FLUSH_FILTERS, this._handleFlushFiltersAction);
        break;
      case DxGridToolbarActionType.SAVE:
        this._toolbarActionHandlers.set(DxGridToolbarActionType.SAVE, this._handleSaveAction);
        break;
      case DxGridToolbarActionType.ADD:
        this._toolbarActionHandlers.set(DxGridToolbarActionType.ADD, this._handleAddAction);
        break;
      case DxGridToolbarActionType.EDIT:
        this._toolbarActionHandlers.set(DxGridToolbarActionType.EDIT, this._handleEditAction);
        break;
      case DxGridToolbarActionType.COPY:
        this._toolbarActionHandlers.set(DxGridToolbarActionType.COPY, this._handleCopyAction);
        break;
      case DxGridToolbarActionType.REMOVE:
        this._toolbarActionHandlers.set(DxGridToolbarActionType.REMOVE, this._handleRemoveAction);
        break;
      case DxGridToolbarActionType.XLSX_EXPORT:
        this._toolbarActionHandlers.set(DxGridToolbarActionType.XLSX_EXPORT, this._handleXlsxExportAction);
        break;
      case DxGridToolbarActionType.PDF_EXPORT:
        this._toolbarActionHandlers.set(DxGridToolbarActionType.PDF_EXPORT, this._handlePdfExportAction);
        break;
      default:
        return;
    }
  }


  /** Обработчик обновления грида. */
  protected _handleRefreshAction(action: DxGridToolbarAction): void {
    if(this._state.hasAddedRows || this._state.hasUpdatedRows) {
      if(this._modalsProvider) {
        let msg = 'В таблице имеются несохраненные изменения! ';
        msg += ' В результате обновления они будут потеряны. ';
        msg += ' Обновить таблицу?';
        this._modalsProvider.showConfirm(msg).subscribe(result => {
          if(result) {
            this.gridRefresh.emit(new DxAppEvent('dx-grid'));
            this.refresh();
          }
        });
      } else {
        this.gridRefresh.emit(new DxAppEvent('dx-grid'));
        this.refresh();
      }
    } else {
      this.gridRefresh.emit(new DxAppEvent('dx-grid'));
      this.refresh();
    }
  }


  /** Обработчик сброса ВСЕХ фитров грида. */
  protected _handleFlushFiltersAction(action: DxGridToolbarAction): void {
    this.filtersFlush.emit(new DxAppEvent('dx-grid'));
    this.flushFilters();
  }


  /**
   * Обработчик сохраненя изменений в гриде:
   * 1. Добавленные строки (новые и копии)
   * 2. Измененные строки
   * 3. Удаленные строки
   *
   * ПС: По-идее, эту функциональность нужно вынести в сервис, но отдельно сервиса в обертке пока не используется.
   * Добавлять в интерфейс датасорса метод, являщийся суперпозицией методов этого датасорса, как то не кошерно.
   */
  protected _handleSaveAction(action: DxGridToolbarAction): void {
    const asyncTasks: Observable<any>[] = [];
    // При серверной модели данных эти массивы заполняются при использвании
    // инлайн-редактирования строк.
    if(this._state.hasAddedRows) {
      asyncTasks.push(this._dataSource.saveAll(this._state.addedRows));
    }
    if(this._state.hasUpdatedRows) {
      asyncTasks.push(this._dataSource.updateAll(this._state.updatedRows));
    }
    // Что касается удаления строчек!!!
    // Пока что грид работает так что при серверной модели данных удаление идет сразу через сервер
    // и this._state.removedRows не заполняется. А при клиенской модели данных сохранение должно идти
    // через внешний механизм. Можно будет добавить на уровне настроек расширенное управленеие удалением
    // при серверной модели данных.
    /*
    if(this._state.hasRemovedRows) {
      const ids: number[] = [];
      this._state.removedRows.forEach(row => ids.push(row.getRowId()));
      asyncTasks.push(this._dataSource.deleteAll(ids));
    }*/

    this.showLoader();
    forkJoin(asyncTasks).subscribe(data => {
      console.log(data);
      this.hideLoader();
      this.refresh();
    }, error => {
      this.showError(error);
    });
  }


  /**
   * Обработчик добавления строки в грид.
   * Механизм добавления новой строки в грид определяется моделью редактирования грида:
   * 1. Если используется инлайн-редактирование, то через фабрику создается экземпляр новой строки
   *    и просто добавляется в грид. Дальше он должен быть отредактирован пользователем и сохранен
   *    через общее сохранение изменений или через внешние механизмы.
   *
   * 2. Если для редактирования используются модальки, то через фабрику создается экземпляр новой строки
   *    и открывается для редактирования в модальной форме. Что произойдет после закрытия модалки нажатием
   *    на "Сохранить", определяется типом модели данных грида:
   *
   *    2.1. При клиентсякой модели данных строка будет добавлена в грид, а также в стейт.
   *    2.2. При серверной модели данных произойдет предварительное сохранение строки через датасорс.
   */
  protected _handleAddAction(action: DxGridToolbarAction): void {
    const newRow: DxGridRow = this._rowFactory.createRow();
    if(this.options.useInlineEdit) {
      // Если используется встроенное редактирование, просто добавляем новую строку в грид
      // пользователь дальше сам ее отредактирует через редактирование ячеек
      // Сохранение пойдет на этапе сохранения измененний в гриде.
      this._state.addedRows.push(newRow);
      this.notifyStateObservers();
      this._agGridApi.updateRowData({add: [newRow], addIndex: 0});
    } else {
      // Предотсавление модалки гарантировано на уровне валидации конфигов.
      // Если пользователь нажал в модальном окне на отмену - вернется нул.
      this._modalsProvider.showEditForm(newRow).subscribe(row => {
        if(row) {
          if(this.options.useServerSideDataModel) {
            this.showLoader();
            this._dataSource.save(row).subscribe(result => {
              this._agGridApi.updateRowData({add: [result], addIndex: 0});
              this.hideLoader();
            }, error => {
              this.showError(error);
            });
          } else {
            this._state.addedRows.push(newRow);
            this.notifyStateObservers();
            this._agGridApi.updateRowData({add: [newRow], addIndex: 0});
          }
        }
      });
    }
  }


  /**
   * Обработчик копирования строки грида.
   * Механизм копирования определяется типом модели данных грида:
   * 1. При клиентской модели данных через фабрику создается копия и добавляется в таблицу.
   *    Также копия помещается в массив добавленных строк.
   *
   * 2. При серверной модели данных через фабрику создается копия и, перед добавлением в грид,
   *    сохраняется через датасорс. Поэтому в стейт копия не добавляется.
   */
  protected _handleCopyAction(action: DxGridToolbarAction): void {
    // Единственность выделения гарантируется панелью инструментов
    const selection: DxGridRow = this._state.selectedRows[0];
    const copy: DxGridRow = this._rowFactory.copyRow(selection);

    // Нужно вычислить индекс для копии в гриде.
    let addIndex = 0;
    const nodes: RowNode[] = this._agGridApi.getSelectedNodes();
    const selectedNode = nodes.find(node => {
      return (<DxGridRow> node.data).getRowId() === selection.getRowId();
    });
    addIndex = selectedNode.rowIndex + 1;

    if(this.options.useServerSideDataModel) {
      // Если используется серверная модель данных, то сначала сохраняем копию на сервере,
      // а потом добавляем результат сохранения в грид.
      this.showLoader();
      this._dataSource.save(copy).subscribe(result => {
        // Копия добавляется сразу после оригинала
        this._agGridApi.deselectAll();
        this._agGridApi.updateRowData({add: [result], addIndex: addIndex});
        this.hideLoader();
      }, error => {
        this.showError(error);
      });
    } else {
      // Если у нас клиентская модель данных, то добавляем копию в грид как новую строку
      // Сохранение пойдет на этапе сохранения изменений в таблице в целом.
      // Копия добавляется сразу после оригинала
      this._state.addedRows.push(copy);
      this.notifyStateObservers();
      this._agGridApi.deselectAll();
      this._agGridApi.updateRowData({add: [copy], addIndex: addIndex});
    }
  }


  /**
   * Обработчик редактирование строки грида.
   * Если грид использует инлайн-модель редактирования, то редактирование пойдет
   * через внутренние механизмы AgGrid и будет активировано через двойной клик, по ячейке строки.
   * Следовательно этот метод будет вызван только при редактировании через модалку.
   *
   * Способ сохранения результатов редактирования зависит оттипа модели данных грида:
   * 1. При клиентской модели данных произойдет апдэйт строки в гриде и добавление ее в массив
   *    обновленных строчек в стэйте.
   *
   * 2. При сервреной модели данных произойдет предварительное сохранение изменений через датасорс.
   *    В стэйт строка добавлена не будет.
   */
  protected _handleEditAction(action: DxGridToolbarAction): void {
    // Единственность выделения гарантируется панелью инструментов
    const selection: DxGridRow = this._state.selectedRows[0];
    // Наличие модалок гарантируется валидацией конфига
    // Если пользователь нажал в модальном окне на отмену - вернется нул.
    this._modalsProvider.showEditForm(selection).subscribe(row => {
      if(row) {
        if(this.options.useServerSideDataModel) {
          this.showLoader();
          this._dataSource.save(row).subscribe(result => {
            this._agGridApi.deselectAll();
            this._agGridApi.updateRowData({update: [result]});
            this.hideLoader();
          }, error => {
            this.showError(error);
          });
        } else {
          this._state.addUpdatedRow(row);
          this.notifyStateObservers();
          this._agGridApi.deselectAll();
          this._agGridApi.updateRowData({update: [row]});
        }
      }
    });
  }


  /**
   * Обработчик удаления строки грида.
   * Способ удаления строчек определяется моделью данных грида:
   * 1. При клиентской модели данных удаленные строчки помещаются в массив удаленных строк в стэйте для последующего
   *    сохранения изменений через внешнее, относительно грида, сохранение (так как датасорс не задан).
   *
   * 2. При серверной модели данных строчки удаляются напрямую через датасорс и накопление удаленных строк
   *    в стэйте грида не происходит.
   */
  protected _handleRemoveAction(action: DxGridToolbarAction): void {
    const selection: DxGridRow[] = [...this._state.selectedRows];
    const remove = () => {
      if(this.options.useServerSideDataModel) {
        if(selection.length === 1) {
          this.showLoader();
          this._dataSource.delete(selection[0].getRowId()).subscribe(id => {
            // TODO: Это только ради клиентской эмуляции
            // TODO: Удалить эту строчку при реализации серверного удаления
            this._agGridDataSource.addRemovedRows(selection);
            this.refresh();
            this.rowsRemove.emit(new DxAppEvent('dx-grid', selection));
          }, error => {
            this.showError(error);
          });
        } else {
          const ids: number[] = [];
          selection.forEach(row => ids.push(row.getRowId()));
          this._dataSource.deleteAll(ids).subscribe(ids => {
            // TODO: Это только ради клиентской эмуляции
            // TODO: Удалить эту строчку при реализации серверного удаления
            this._agGridDataSource.addRemovedRows(selection);
            this.refresh();
            this.rowsRemove.emit(new DxAppEvent('dx-grid', selection));
          }, error => {
            this.showError(error);
          });
        }
      } else {
        // Если используется клиентская модель данных, то просто удаляем строки из гида
        // и добавляем их добавляем в массив удаленых строк в стейте чтоб внешний клиент
        // грида мог их как-то удалить например.
        selection.forEach(row => {
          row.isSelected = false;
          this._state.removedRows.push(row);
        });
        this._state.clearSelected();
        this.notifyStateObservers();
        this._agGridApi.updateRowData({remove: selection});
        this.rowsRemove.emit(new DxAppEvent('dx-grid', selection));
      }
    };

    if(this._modalsProvider) {
      let msg = 'Вы действительно хотите удалить ';
      const elements: string[] = [];
      selection.forEach(row => elements.push(row.getRowTitle()));
      msg += elements.join(', ') + '?';

      this._modalsProvider.showConfirm(msg).subscribe(confirmed => {
        if(confirmed) remove();
      })
    } else {
      remove();
    }
  }


  /** Обработчик экспорта в .xlsx. */
  protected _handleXlsxExportAction(action: DxGridToolbarAction): void {
    const columns = this.options.columns;
    const filters = AgGridUtils.prepareFilters(columns, this._agGridApi.getFilterModel(), this._gridDicts);
    const sorts = AgGridUtils.prepareSorts(columns, this._agGridApi.getSortModel());

    // Для серверной модели данных эксельник должен формироваться на сервере :)
    // Истинен только путь сей :)
    if(this.options.useServerSideDataModel) {
      this.showLoader();
      this._dataSource.exportXlsx(this.options, filters, sorts).subscribe(xlsx => {
        this.hideLoader();
      }, error => {
        this.showError(error);
      });
    } else {
      // Может выполнятся долго
      this.showLoader();
      DxGridUtils.exportXlsx(this.options, this._rows, filters, sorts, this._gridDicts);
      this.hideLoader();
    }
  }


  /** Обработчик экспорта в .pdf. */
  protected _handlePdfExportAction(action: DxGridToolbarAction): void {

  }


  protected _handleRowDataLoad(isLoading: boolean): void {
    if(isLoading) {
      this.showLoader()
    } else {
      this.hideLoader();
    }
  }

  protected _handleRowDataLoadError(error: DxAppError): void {
    this.showError(error.message);
  }

  protected _fetchDicts(): Observable<Map<string, DxNciDictionary>> {
    const idents: string[] = [];
    this.options.columns
      .filter(column => column.dataType.isDictionary)
      .forEach(column => idents.push(column.dataType.dictIdent));

    return this._nciService.fetchDictionaries(idents).pipe(map((dictionaries: DxNciDictionary[]) => {
      const dictsMap: Map<string, DxNciDictionary> = new Map();
      dictionaries.forEach(dict => dictsMap.set(dict.ident, dict));
      return dictsMap;
    }));
  }

  /**
   * Метод (api) для серверной модели с datasource, который позволяет итерировать по пришедшим с сервера данным.
   * Это оффициальный подход описанный тут: https://www.ag-grid.com/javascript-grid-accessing-data/
   */
  forEachDisplayed(callbackFn: (row) => any) {
    const rowCount = this._agGridApi.getDisplayedRowCount();
    const lastGridIndex = rowCount - 1;
    const currentPage = this._agGridApi.paginationGetCurrentPage();
    const pageSize = this._agGridApi.paginationGetPageSize();
    const startPageIndex = currentPage * pageSize;
    let endPageIndex = (currentPage + 1) * pageSize - 1;
    if (endPageIndex > lastGridIndex) {
      endPageIndex = lastGridIndex;
    }
    const rowNodes = [];

    for (let i = startPageIndex; i <= endPageIndex; i++) {
      const rowNode = this._agGridApi.getDisplayedRowAtIndex(i);
      if (!rowNode.data) {
        // WARNING: при частом обращении за данными из datasource, данные в rowNode пропадают!
        // это косяк имплементации
        return;
      }
      callbackFn(rowNode.data);
      rowNodes.push(rowNode);
    }
    this._agGridApi.redrawRows({rowNodes});
  }

}
